import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser, getAgents } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const AddUser = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingAgents, setLoadingAgents] = useState(true);
    const [validated, setValidated] = useState(false);
    const [error, setError] = useState('');
    const [agents, setAgents] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        gender: '',
        mobileNumber: '',
        password: '',
        referedBy: '',
        activeAgentId: ''
    });

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            setLoadingAgents(true);
            const response = await getAgents({ page: 1, limit: 1000 });
            if (response.success || response.data) {
                const agentsData = Array.isArray(response.data) ? response.data : (response.data?.agents || []);
                setAgents(agentsData);
            }
        } catch (error) {
            console.error('Error fetching agents:', error);
            setError('Failed to load agents. Please refresh the page.');
        } finally {
            setLoadingAgents(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }
        
        try {
            setLoading(true);
            setError('');
            const payload = {
                ...formData,
                agentId: formData.activeAgentId
            };
            delete payload.activeAgentId;
            
            await registerUser(payload);
            alert('User added successfully');
            navigate('/admin/users');
        } catch (error) {
            setError(error.message || 'Failed to add user');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Add User" sidebutton={true} />
            <div className="row">
                <div className="col-xl-8 col-lg-10 mx-auto">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom py-3">
                            <div className="d-flex align-items-center">
                                <i className="fa fa-user-plus text-primary me-2" style={{ fontSize: '20px' }}></i>
                                <Card.Title className="mb-0 fw-bold">Add New User</Card.Title>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {error && (
                                <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
                                    <i className="fa fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Full Name <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter full name"
                                                required
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter full name
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Email <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Enter email address"
                                                required
                                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid email address
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Gender <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleChange}
                                                required
                                                className="form-control-lg"
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                Please select gender
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Mobile Number <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mobileNumber"
                                                value={formData.mobileNumber}
                                                onChange={handleChange}
                                                placeholder="Enter mobile number"
                                                pattern="[0-9]{10}"
                                                required
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid 10-digit mobile number
                                            </Form.Control.Feedback>
                                            <Form.Text className="text-muted">
                                                10 digits required
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Password <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Enter password"
                                                required
                                                minLength={6}
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Password must be at least 6 characters long
                                            </Form.Control.Feedback>
                                            <Form.Text className="text-muted">
                                                Minimum 6 characters required
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Active Agent ID <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="activeAgentId"
                                                value={formData.activeAgentId}
                                                onChange={handleChange}
                                                required
                                                className="form-control-lg"
                                                disabled={loadingAgents}
                                            >
                                                <option value="">{loadingAgents ? 'Loading agents...' : 'Select Agent'}</option>
                                                {agents.map((agent) => (
                                                    <option key={agent._id} value={agent.agentId || agent._id}>
                                                        {agent.agentId || agent._id} {agent.name ? `- ${agent.name}` : ''} {agent.email ? `(${agent.email})` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                Please select an agent
                                            </Form.Control.Feedback>
                                            {agents.length === 0 && !loadingAgents && (
                                                <Form.Text className="text-warning">
                                                    <i className="fa fa-exclamation-triangle me-1"></i> No agents available. Please create an agent first.
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Referral Code <span className="text-muted">(Optional)</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="referedBy"
                                                value={formData.referedBy}
                                                onChange={handleChange}
                                                placeholder="Enter referral code"
                                                className="form-control-lg"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/admin/users')}
                                        className="px-4"
                                    >
                                        <i className="fa fa-times me-2"></i> Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={loading}
                                        className="px-4"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-user-plus me-2"></i> Create User
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
            
            <style>{`
                .form-control-lg:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
                }
                .card {
                    transition: transform 0.2s ease-in-out;
                }
                .form-label {
                    color: #495057;
                }
                .text-danger {
                    font-weight: 600;
                }
            `}</style>
        </div>
    );
};

export default AddUser;
